const CART_SIZE_QUANTITIES_STORAGE_KEY = 'fp_cart_item_size_quantities';
const CART_NEWLY_ADDED_DESIGNS_STORAGE_KEY = 'fp_cart_newly_added_design_ids';

type ICartSizeQuantitiesByUser = Record<string, Record<string, Record<string, number>>>;
type ICartNewlyAddedDesignIdsByUser = Record<string, number[]>;

function readStorageMap(): ICartSizeQuantitiesByUser {
  try {
    const raw = localStorage.getItem(CART_SIZE_QUANTITIES_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ICartSizeQuantitiesByUser;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStorageMap(map: ICartSizeQuantitiesByUser): void {
  try {
    localStorage.setItem(CART_SIZE_QUANTITIES_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Ignore quota / private-mode errors
  }
}

function toUserKey(userId: number | string | undefined): string | null {
  if (userId == null || userId === '') return null;
  return String(userId);
}

/** Reads persisted per-size quantities for a cart line (survives modal close and login). */
export function readCartItemSizeQuantities(
  userId: number | string | undefined,
  cartItemId: number
): Record<string, number> {
  const userKey = toUserKey(userId);
  if (!userKey || !cartItemId) return {};

  const stored = readStorageMap()[userKey]?.[String(cartItemId)];
  if (!stored || typeof stored !== 'object') return {};

  const result: Record<string, number> = {};
  Object.entries(stored).forEach(([label, qty]) => {
    const parsed = typeof qty === 'number' ? qty : Number(qty);
    if (Number.isFinite(parsed) && parsed > 0) {
      result[label] = parsed;
    }
  });

  return result;
}

/** Persists per-size quantities for a cart line. Pass empty map to clear. */
export function writeCartItemSizeQuantities(
  userId: number | string | undefined,
  cartItemId: number,
  sizes: Record<string, string | number | undefined>
): void {
  const userKey = toUserKey(userId);
  if (!userKey || !cartItemId) return;

  const normalized: Record<string, number> = {};
  Object.entries(sizes).forEach(([label, rawQty]) => {
    const qty = typeof rawQty === 'number' ? rawQty : parseInt(String(rawQty ?? ''), 10);
    if (Number.isFinite(qty) && qty > 0) {
      normalized[label] = qty;
    }
  });

  const map = readStorageMap();
  if (!map[userKey]) {
    map[userKey] = {};
  }

  const itemKey = String(cartItemId);
  if (Object.keys(normalized).length === 0) {
    delete map[userKey][itemKey];
  } else {
    map[userKey][itemKey] = normalized;
  }

  writeStorageMap(map);
}

export function clearCartItemSizeQuantities(
  userId: number | string | undefined,
  cartItemId: number
): void {
  writeCartItemSizeQuantities(userId, cartItemId, {});
}

function readNewlyAddedDesignIdsMap(): ICartNewlyAddedDesignIdsByUser {
  try {
    const raw = localStorage.getItem(CART_NEWLY_ADDED_DESIGNS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ICartNewlyAddedDesignIdsByUser;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeNewlyAddedDesignIdsMap(map: ICartNewlyAddedDesignIdsByUser): void {
  try {
    localStorage.setItem(CART_NEWLY_ADDED_DESIGNS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // Ignore quota / private-mode errors
  }
}

/** Marks a design so the next cart form load prefills the first size with default MOQ (add-to-cart only). */
export function markDesignForDefaultSizePrefill(
  userId: number | string | undefined,
  designId: number
): void {
  const userKey = toUserKey(userId);
  if (!userKey || !designId) return;

  const map = readNewlyAddedDesignIdsMap();
  const existing = map[userKey] ?? [];
  if (existing.includes(designId)) return;

  map[userKey] = [...existing, designId];
  writeNewlyAddedDesignIdsMap(map);
}

/**
 * Returns true once per marked design — used to apply default first-size qty only right after add-to-cart.
 */
export function consumeDesignDefaultSizePrefill(
  userId: number | string | undefined,
  designId: number
): boolean {
  const userKey = toUserKey(userId);
  if (!userKey || !designId) return false;

  const map = readNewlyAddedDesignIdsMap();
  const existing = map[userKey];
  if (!existing?.includes(designId)) return false;

  map[userKey] = existing.filter((id) => id !== designId);
  if (map[userKey].length === 0) {
    delete map[userKey];
  }
  writeNewlyAddedDesignIdsMap(map);
  return true;
}
